import { SPIKE_THRESHOLD } from "app/insights-assistant/insights-types";

import type { INotableChange, NotableChangeKind } from "./types";

export const MAX_NOTABLE_CHANGES = 2;

/**
 * Breakdown items below this share of total visits are excluded before ranking — a +400% move on a
 * channel worth 0.1% of traffic is noise, not a story.
 */
export const NOTABLE_CHANGE_MIN_SHARE = 0.02;

/** A move of this size or larger scores full magnitude; smaller moves scale linearly towards it. */
const CHANGE_SATURATION = SPIKE_THRESHOLD * 3;

/**
 * Site-wide metrics have no traffic share to weight them by, so they get a fixed impact calibrated
 * to rank between a mid-sized and a large breakdown mover rather than always winning.
 */
const SITE_METRIC_IMPACT = 0.05;

const magnitude = (changeRatio: number): number => Math.min(Math.abs(changeRatio) / CHANGE_SATURATION, 1);

/** A change qualifies as notable once it clears the same threshold the Marketing Channels insights use. */
export const isNotableChange = (changeRatio: number | null | undefined): changeRatio is number =>
    changeRatio != null && Number.isFinite(changeRatio) && Math.abs(changeRatio) >= SPIKE_THRESHOLD;

/** Weights a breakdown mover (channel, country) by how much of the site's traffic its move accounts for. */
export const scoreBreakdownChange = (changeRatio: number, share: number): number => magnitude(changeRatio) * share;

export const scoreSiteMetricChange = (changeRatio: number): number => magnitude(changeRatio) * SITE_METRIC_IMPACT;

/**
 * Ranks candidates by significance and returns the top {@link MAX_NOTABLE_CHANGES}, keeping at most one
 * item per kind so the card never shows two countries and nothing else.
 */
export const rankNotableChanges = (candidates: INotableChange[]): INotableChange[] => {
    const bestPerKind = new Map<NotableChangeKind, INotableChange>();

    candidates.forEach((candidate) => {
        const incumbent = bestPerKind.get(candidate.kind);
        if (!incumbent || candidate.significance > incumbent.significance) {
            bestPerKind.set(candidate.kind, candidate);
        }
    });

    return [...bestPerKind.values()].sort((a, b) => b.significance - a.significance).slice(0, MAX_NOTABLE_CHANGES);
};
